import React, { useEffect, useMemo, useState } from 'react';
import logo from '../../assets/logo-lockup.png';
import { renderInline } from './legalMarkdown';
import LegalDocumentView, { publicLegalBlocks } from './LegalDocumentView';
import LegalFooterLinks from './LegalFooterLinks';
import { usePublishedLegalDocument } from '../../agents/legalDocumentsAgent';
import styles from './LegalPage.module.css';

/**
 * LegalPage — public, full-page rendering of a legal document (/privacy,
 * /terms). Rendered by App.js from the pathname, outside the auth gate, so it
 * works for signed-out visitors (e.g. from the sign-up disclaimer links).
 *
 * The text is the current published version from the backend
 * (GET /api/legal/documents/{key}); admins edit and publish versions from the
 * Admin → Legal documents tab.
 *
 * @param {object} props
 * @param {object} props.document - Entry from LEGAL_DOCUMENTS ({key, path, title}).
 */
const LegalPage = ({ document: legalDoc }) => {
  const { data, loading, notFound, retry } = usePublishedLegalDocument(legalDoc.key);

  // Counsel notes are stripped server-side; filtering again is defence in depth.
  const blocks = useMemo(() => (data ? publicLegalBlocks(data.body_markdown) : []), [data]);
  const sections = blocks.filter((block) => block.type === 'h2');
  // Only a counsel-approved version may be indexed or shown without the badge;
  // while loading or on error we stay conservative.
  const approved = Boolean(data && data.counsel_approved === true);
  const isDraft = Boolean(data) && !approved;
  // Below 1024px the contents list collapses behind a toggle so the document
  // starts on the first screen; on wider screens it is always shown (CSS).
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const previousTitle = window.document.title;
    window.document.title = `${legalDoc.title}${isDraft ? ' (Draft)' : ''} — cutthe6.com`;

    // Keep anything that isn't a counsel-approved version out of search results.
    let robots = null;
    if (!approved) {
      robots = window.document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'noindex';
      window.document.head.appendChild(robots);
    }

    return () => {
      window.document.title = previousTitle;
      if (robots) robots.remove();
    };
  }, [legalDoc.title, isDraft, approved]);

  // The document renders after the browser's own hash scroll (it arrives from
  // the API), so honour deep links like /terms#8-disclaimers ourselves.
  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const target = window.document.getElementById(decodeURIComponent(hash));
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView();
  }, [data]);

  let content;
  if (data) {
    content = (
      <LegalDocumentView
        blocks={blocks}
        fallbackTitle={data.title || legalDoc.title}
        version={data.version}
        publishedAt={data.published_at}
        counselApproved={approved}
      />
    );
  } else if (loading) {
    content = (
      <article className={styles.article} aria-busy="true">
        <h1 className={styles.title}>{legalDoc.title}</h1>
        <div className={styles.statusBox} role="status" data-testid="legal-loading">
          <span className={styles.spinner} aria-hidden="true" />
          Loading {legalDoc.title}…
        </div>
        <div className={styles.skeleton} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </article>
    );
  } else {
    content = (
      <article className={styles.article}>
        <h1 className={styles.title}>{legalDoc.title}</h1>
        <div className={styles.errorBox} role="alert" data-testid="legal-error">
          {notFound ? (
            <p className={styles.errorText}>
              The {legalDoc.title} hasn&rsquo;t been published yet. Please check back soon.
            </p>
          ) : (
            <p className={styles.errorText}>
              We couldn&rsquo;t load the {legalDoc.title}. Check your connection and try again.
            </p>
          )}
          {!notFound && (
            <button type="button" className={styles.retryButton} onClick={retry}>
              Retry
            </button>
          )}
        </div>
      </article>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="/" className={styles.brand} aria-label="cutthe6.com home">
          <img src={logo} alt="cutthe6.com" className={styles.brandLogo} />
        </a>
        <a href="/" className={styles.backLink}>Back to cutthe6.com</a>
      </header>

      <div className={`${styles.layout} ${sections.length === 0 ? styles.layoutSingle : ''}`.trim()}>
        {sections.length > 0 && (
          <nav className={styles.toc} aria-label="On this page">
            <p className={styles.tocHeading}>On this page</p>
            <button
              type="button"
              className={styles.tocToggle}
              aria-expanded={tocOpen}
              aria-controls="legal-toc-list"
              onClick={() => setTocOpen((open) => !open)}
            >
              On this page
              <span className={styles.tocCount}>{sections.length} sections</span>
              <span className={`${styles.tocCaret} ${tocOpen ? styles.tocCaretOpen : ''}`} aria-hidden="true">▾</span>
            </button>
            <ol id="legal-toc-list" className={`${styles.tocList} ${tocOpen ? styles.tocListOpen : ''}`}>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className={styles.tocLink} onClick={() => setTocOpen(false)}>
                    {renderInline(section.text, `toc-${section.id}`)}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {content}
      </div>

      <footer className={styles.footer}>
        <span>© 2026 cutthe6.com</span>
        <LegalFooterLinks newTab={false} />
      </footer>
    </div>
  );
};

export default LegalPage;
